import React, { useState, useEffect } from 'react';
import { 
  Lightbulb, ChevronLeft, Plus, Edit2, Trash2, Search, Filter,
  Check, X, RefreshCw, Eye, Save
} from 'lucide-react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from '../hooks/use-toast';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';

const TIPS_TYPES = [
  { id: 'conseil', name: 'Conseil', color: '#4CAF50' },
  { id: 'saviez_vous', name: 'Le saviez-vous ?', color: '#2196F3' },
  { id: 'mythe', name: 'Mythe vs Réalité', color: '#FF5722' },
  { id: 'astuce_naturelle', name: 'Astuce naturelle', color: '#8BC34A' },
];

const AdminTipsManager = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [customTips, setCustomTips] = useState([]);
  const [staticTips, setStaticTips] = useState([]);
  const [categories, setCategories] = useState([]);
  const [activeTab, setActiveTab] = useState('custom');
  const [searchQuery, setSearchQuery] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterType, setFilterType] = useState('all');
  
  // Dialog states
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showPreviewDialog, setShowPreviewDialog] = useState(false);
  const [selectedTip, setSelectedTip] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    category: '',
    type: 'conseil',
    source: ''
  });

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await api.get('/admin/tips');
      setCustomTips(response.data.customTips || []);
      setStaticTips(response.data.staticTips || []);
      setCategories(response.data.categories || []);
    } catch (error) {
      console.error('Error fetching tips:', error);
      toast({
        title: 'Erreur',
        description: 'Impossible de charger les astuces',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleCreate = async () => {
    if (!formData.title || !formData.content || !formData.category) {
      toast({
        title: 'Erreur',
        description: 'Veuillez remplir tous les champs obligatoires',
        variant: 'destructive'
      });
      return;
    }

    try {
      await api.post('/admin/tips', formData);
      toast({ title: 'Succès', description: 'Astuce créée avec succès' });
      setShowCreateDialog(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast({
        title: 'Erreur',
        description: error.response?.data?.detail || 'Erreur lors de la création',
        variant: 'destructive'
      });
    }
  };

  const handleUpdate = async () => {
    if (!selectedTip || !formData.title || !formData.content) {
      toast({
        title: 'Erreur',
        description: 'Veuillez remplir tous les champs obligatoires',
        variant: 'destructive'
      });
      return;
    }

    try {
      await api.put(`/admin/tips/${selectedTip.id}`, formData);
      toast({ title: 'Succès', description: 'Astuce mise à jour' });
      setShowEditDialog(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast({
        title: 'Erreur',
        description: error.response?.data?.detail || 'Erreur lors de la mise à jour',
        variant: 'destructive'
      });
    }
  };

  const handleDelete = async (tipId) => {
    if (!window.confirm('Supprimer cette astuce ?')) return;

    try {
      await api.delete(`/admin/tips/${tipId}`);
      toast({ title: 'Supprimé', description: 'Astuce supprimée' });
      fetchData();
    } catch (error) {
      toast({
        title: 'Erreur',
        description: 'Impossible de supprimer cette astuce',
        variant: 'destructive'
      });
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      content: '',
      category: '',
      type: 'conseil',
      source: ''
    });
    setSelectedTip(null);
  };

  const openEditDialog = (tip) => {
    setSelectedTip(tip);
    setFormData({
      title: tip.title,
      content: tip.content,
      category: tip.category,
      type: tip.type,
      source: tip.source || ''
    });
    setShowEditDialog(true);
  };

  const openPreviewDialog = (tip) => {
    setSelectedTip(tip);
    setShowPreviewDialog(true);
  };

  const getCategoryName = (categoryId) => {
    const cat = categories.find(c => c.id === categoryId);
    return cat ? cat.name : categoryId;
  };

  const getCategoryIcon = (categoryId) => {
    const cat = categories.find(c => c.id === categoryId);
    return cat ? cat.icon : '💡';
  };

  const getTypeName = (typeId) => {
    const type = TIPS_TYPES.find(t => t.id === typeId);
    return type ? type.name : typeId;
  };

  const getTypeColor = (typeId) => {
    const type = TIPS_TYPES.find(t => t.id === typeId);
    return type ? type.color : '#888';
  };

  // Filter tips
  const filterTips = (tips) => {
    return tips.filter(tip => {
      const matchesSearch = searchQuery === '' || 
        tip.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        tip.content.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesCategory = filterCategory === 'all' || tip.category === filterCategory;
      const matchesType = filterType === 'all' || tip.type === filterType;
      return matchesSearch && matchesCategory && matchesType;
    });
  };

  const TipCard = ({ tip, isCustom }) => (
    <Card className="p-4 bg-white/10 backdrop-blur-md border-white/20 hover:bg-white/15 transition-all">
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-2xl">{getCategoryIcon(tip.category)}</span>
            <h3 className="text-white font-semibold line-clamp-1">{tip.title}</h3>
          </div>
          <p className="text-pink-200 text-sm line-clamp-2 mb-2">{tip.content}</p>
          <div className="flex items-center gap-2 flex-wrap">
            <span 
              className="px-2 py-0.5 rounded-full text-xs text-white"
              style={{ backgroundColor: getTypeColor(tip.type) }}
            >
              {getTypeName(tip.type)}
            </span>
            <span className="text-pink-300 text-xs">{getCategoryName(tip.category)}</span>
            {tip.source && (
              <span className="text-pink-400 text-xs">• {tip.source}</span>
            )}
          </div>
        </div>
        <div className="flex flex-col gap-1">
          <Button
            size="sm"
            variant="ghost"
            onClick={() => openPreviewDialog(tip)}
            className="text-blue-400 hover:bg-blue-500/20 h-8 w-8 p-0"
          >
            <Eye className="w-4 h-4" />
          </Button>
          {isCustom && (
            <>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => openEditDialog(tip)}
                className="text-yellow-400 hover:bg-yellow-500/20 h-8 w-8 p-0"
              >
                <Edit2 className="w-4 h-4" />
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => handleDelete(tip.id)}
                className="text-red-400 hover:bg-red-500/20 h-8 w-8 p-0"
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </>
          )}
        </div>
      </div>
    </Card>
  );

  const TipForm = ({ onSubmit, submitLabel }) => (
    <div className="space-y-4">
      <div>
        <Label className="text-pink-200">Titre *</Label>
        <Input
          value={formData.title}
          onChange={(e) => setFormData({ ...formData, title: e.target.value })}
          placeholder="Ex: Bouillotte chaude contre les crampes"
          className="bg-white/10 border-white/20 text-white mt-1"
        />
      </div>
      <div>
        <Label className="text-pink-200">Contenu *</Label>
        <Textarea
          value={formData.content}
          onChange={(e) => setFormData({ ...formData, content: e.target.value })}
          placeholder="Description détaillée de l'astuce..."
          className="bg-white/10 border-white/20 text-white mt-1 min-h-[120px]"
        />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label className="text-pink-200">Catégorie *</Label>
          <Select
            value={formData.category}
            onValueChange={(val) => setFormData({ ...formData, category: val })}
          >
            <SelectTrigger className="bg-white/10 border-white/20 text-white mt-1">
              <SelectValue placeholder="Choisir..." />
            </SelectTrigger>
            <SelectContent>
              {categories.map((cat) => (
                <SelectItem key={cat.id} value={cat.id}>
                  {cat.icon} {cat.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-pink-200">Type</Label>
          <Select
            value={formData.type}
            onValueChange={(val) => setFormData({ ...formData, type: val })}
          >
            <SelectTrigger className="bg-white/10 border-white/20 text-white mt-1">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {TIPS_TYPES.map((type) => (
                <SelectItem key={type.id} value={type.id}>
                  {type.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
      <div>
        <Label className="text-pink-200">Source (optionnel)</Label>
        <Input
          value={formData.source}
          onChange={(e) => setFormData({ ...formData, source: e.target.value })}
          placeholder="Ex: OMS, Médecine traditionnelle..."
          className="bg-white/10 border-white/20 text-white mt-1"
        />
      </div>
      <Button onClick={onSubmit} className="w-full bg-gradient-to-r from-pink-500 to-purple-500">
        <Save className="w-4 h-4 mr-2" />
        {submitLabel}
      </Button>
    </div>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-900 via-pink-800 to-purple-900 flex items-center justify-center">
        <div className="text-white text-xl">Chargement...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-900 via-pink-800 to-purple-900 pb-8">
      {/* Header */}
      <div className="sticky top-0 bg-purple-900/80 backdrop-blur-md border-b border-white/10 px-4 py-4 z-40">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/admin')} className="p-2 hover:bg-white/10 rounded-lg">
              <ChevronLeft className="w-6 h-6 text-white" />
            </button>
            <div className="flex items-center gap-2">
              <Lightbulb className="w-6 h-6 text-yellow-400" />
              <h1 className="text-xl font-bold text-white">Gestion des Astuces</h1>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button 
              variant="ghost" 
              size="sm"
              onClick={fetchData}
              className="text-white hover:bg-white/10"
            >
              <RefreshCw className="w-4 h-4" />
            </Button>
            <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
              <DialogTrigger asChild>
                <Button size="sm" className="bg-pink-500 hover:bg-pink-600">
                  <Plus className="w-4 h-4 mr-1" />
                  Nouvelle astuce
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-purple-900 border-purple-700 max-w-lg max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle className="text-white text-xl flex items-center gap-2">
                    <Lightbulb className="w-6 h-6 text-yellow-400" />
                    Créer une nouvelle astuce
                  </DialogTitle>
                </DialogHeader>
                <TipForm onSubmit={handleCreate} submitLabel="Créer l'astuce" />
              </DialogContent>
            </Dialog>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-6">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <Card className="p-4 bg-white/10 backdrop-blur-md border-white/20 text-center">
            <p className="text-3xl font-bold text-yellow-400">{customTips.length}</p>
            <p className="text-pink-200 text-sm">Astuces personnalisées</p>
          </Card>
          <Card className="p-4 bg-white/10 backdrop-blur-md border-white/20 text-center">
            <p className="text-3xl font-bold text-green-400">{staticTips.length}</p>
            <p className="text-pink-200 text-sm">Astuces prédéfinies</p>
          </Card>
          <Card className="p-4 bg-white/10 backdrop-blur-md border-white/20 text-center">
            <p className="text-3xl font-bold text-blue-400">{categories.length}</p>
            <p className="text-pink-200 text-sm">Catégories</p>
          </Card>
          <Card className="p-4 bg-white/10 backdrop-blur-md border-white/20 text-center">
            <p className="text-3xl font-bold text-purple-400">{customTips.length + staticTips.length}</p>
            <p className="text-pink-200 text-sm">Total astuces</p>
          </Card>
        </div>

        {/* Filters */}
        <Card className="p-4 bg-white/10 backdrop-blur-md border-white/20 mb-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-pink-300" />
                <Input
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Rechercher une astuce..."
                  className="bg-white/10 border-white/20 text-white pl-10"
                />
              </div>
            </div>
            <Select value={filterCategory} onValueChange={setFilterCategory}>
              <SelectTrigger className="bg-white/10 border-white/20 text-white w-full md:w-48">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Catégorie" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Toutes catégories</SelectItem>
                {categories.map((cat) => (
                  <SelectItem key={cat.id} value={cat.id}>
                    {cat.icon} {cat.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={filterType} onValueChange={setFilterType}>
              <SelectTrigger className="bg-white/10 border-white/20 text-white w-full md:w-48">
                <SelectValue placeholder="Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous types</SelectItem>
                {TIPS_TYPES.map((type) => (
                  <SelectItem key={type.id} value={type.id}>
                    {type.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </Card>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-2 bg-white/10 mb-6">
            <TabsTrigger 
              value="custom" 
              className="data-[state=active]:bg-pink-500/50"
            >
              Personnalisées ({filterTips(customTips).length})
            </TabsTrigger>
            <TabsTrigger 
              value="static"
              className="data-[state=active]:bg-pink-500/50"
            >
              Prédéfinies ({filterTips(staticTips).length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="custom">
            {filterTips(customTips).length > 0 ? (
              <div className="grid gap-4 md:grid-cols-2">
                {filterTips(customTips).map((tip) => (
                  <TipCard key={tip.id} tip={tip} isCustom={true} />
                ))}
              </div>
            ) : (
              <Card className="p-8 bg-white/10 border-white/20 text-center">
                <Lightbulb className="w-12 h-12 text-yellow-400/50 mx-auto mb-4" />
                <h3 className="text-white font-semibold mb-2">Aucune astuce personnalisée</h3>
                <p className="text-pink-200 text-sm mb-4">
                  Créez votre première astuce personnalisée
                </p>
                <Button onClick={() => setShowCreateDialog(true)} className="bg-pink-500">
                  <Plus className="w-4 h-4 mr-2" />
                  Créer une astuce
                </Button>
              </Card>
            )}
          </TabsContent>

          <TabsContent value="static">
            {filterTips(staticTips).length > 0 ? (
              <div className="grid gap-4 md:grid-cols-2">
                {filterTips(staticTips).map((tip) => (
                  <TipCard key={tip.id} tip={tip} isCustom={false} />
                ))}
              </div>
            ) : (
              <Card className="p-8 bg-white/10 border-white/20 text-center">
                <p className="text-pink-200">Aucune astuce trouvée avec ces filtres</p>
              </Card>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* Edit Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="bg-purple-900 border-purple-700 max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white text-xl flex items-center gap-2">
              <Edit2 className="w-6 h-6 text-yellow-400" />
              Modifier l'astuce
            </DialogTitle>
          </DialogHeader>
          <TipForm onSubmit={handleUpdate} submitLabel="Enregistrer les modifications" />
        </DialogContent>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog open={showPreviewDialog} onOpenChange={setShowPreviewDialog}>
        <DialogContent className="bg-purple-900 border-purple-700 max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-white text-xl flex items-center gap-2">
              <Eye className="w-6 h-6 text-blue-400" />
              Aperçu
            </DialogTitle>
          </DialogHeader>
          {selectedTip && (
            <Card className="p-6 bg-white/10 border-white/20">
              <div className="flex items-center gap-3 mb-4">
                <span className="text-4xl">{getCategoryIcon(selectedTip.category)}</span>
                <div>
                  <h3 className="text-white font-bold text-lg">{selectedTip.title}</h3>
                  <span 
                    className="px-2 py-0.5 rounded-full text-xs text-white"
                    style={{ backgroundColor: getTypeColor(selectedTip.type) }}
                  >
                    {getTypeName(selectedTip.type)}
                  </span>
                </div>
              </div>
              <p className="text-pink-100 leading-relaxed mb-4">{selectedTip.content}</p>
              <div className="flex items-center justify-between text-sm">
                <span className="text-pink-300">{getCategoryName(selectedTip.category)}</span>
                {selectedTip.source && (
                  <span className="text-pink-400 italic">Source: {selectedTip.source}</span>
                )}
              </div>
            </Card>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminTipsManager;
